import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiChevronRight, FiChevronUp, FiSliders } from "react-icons/fi";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { EMP_BASE_PATH } from "../../config";
import {
  getMyYearlyEvaluationsApi,
  confirmMyEvaluationApi,
} from "../../api/evaluation.api";
import {
  buildYearOptions,
  getConfirmWindow,
  getEvaluationStatusLabel,
  getEvaluationStatusUi,
} from "./evaluationConstants";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const BORDER = "#E1E1E1";
const LINE = "#EEF1F6";

const RowKV = ({ label, value }) => (
  <Flex align="flex-start" gap="12px" py="4px">
    <Text fontSize="14px" color={MUTED} flex="0.42">
      {label}
    </Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="0.58" textAlign="right" noOfLines={1}>
      {value}
    </Text>
  </Flex>
);

const EvaluationCard = ({ item, confirmWindow, onOpen, onConfirm }) => {
  const ui = getEvaluationStatusUi(item.evaluationStatus);
  const label = getEvaluationStatusLabel(item.evaluationStatus);

  const isPrevMonth =
    item.month === confirmWindow.prevMonth && item.year === confirmWindow.prevYear;
  const isCurrentMonth =
    item.month === confirmWindow.currentMonth && item.year === confirmWindow.currentYear;
  const canConfirm = !item.saleConfirmed && isPrevMonth && confirmWindow.isOpen;

  return (
    <Box
      onClick={onOpen}
      cursor="pointer"
      border="1px solid"
      borderColor={BORDER}
      borderRadius="12px"
      bg="white"
      p="16px"
    >
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">
          Đánh giá tháng {item.month}/{item.year}
        </Text>
        <Box bg={ui.bg} color={ui.color} borderRadius="6px" px="8px" py="3px" fontSize="12px" fontWeight={600}>
          {label}
        </Box>
      </Flex>

      <Box h="1px" bg={LINE} my="12px" />

      <RowKV label="Số công" value={`${item.workingDayRate ?? 0}/${item.standardWorkingDay ?? 0}`} />
      <RowKV label="Tư vấn" value={String(item.consultCount ?? 0)} />
      <RowKV label="Leads" value={String(item.leadCount ?? 0)} />
      <RowKV label="Giao dịch" value={String(item.transactionCount ?? 0)} />

      <Box h="1px" bg={LINE} my="12px" />

      <Flex align="center" justify="space-between">
        {item.saleConfirmed ? (
          <Box bg="#EDFDF5" color="#2ABA82" px="10px" py="6px" borderRadius="6px" fontSize="12px" fontWeight={600}>
            Đã xác nhận
          </Box>
        ) : canConfirm ? (
          <Box
            as="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            bg={APP.primary200}
            color="white"
            px="16px"
            py="8px"
            borderRadius="8px"
            fontSize="13px"
            fontWeight={700}
          >
            Xác nhận
          </Box>
        ) : isCurrentMonth ? (
          <Box bg="#FFF7E8" color="#F79D0D" px="10px" py="6px" borderRadius="6px" fontSize="12px" fontWeight={600}>
            Chức năng xác nhận đang bị khoá
          </Box>
        ) : (
          <Text fontSize="14px" color={MUTED}>
            Chưa xác nhận
          </Text>
        )}

        <Flex align="center" gap="2px" color={APP.primary200} fontWeight={600} fontSize="12px">
          Chi tiết
          <FiChevronRight size={16} />
        </Flex>
      </Flex>
    </Box>
  );
};

const MyEvaluationList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const toast = useToast();
  const yearSheet = useDisclosure();
  const [filterOpen, setFilterOpen] = useState(false);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(() => buildYearOptions(), []);
  const confirmWindow = useMemo(() => getConfirmWindow(), []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMyYearlyEvaluationsApi(year);
      setItems(Array.isArray(data) ? data : data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onConfirm = useCallback(
    async (item) => {
      try {
        await confirmMyEvaluationApi({ month: item.month, year: item.year });
        toast({ title: "Xác nhận đánh giá thành công", status: "success", duration: 2500, position: "top" });
        fetchList();
      } catch (e) {
        toast({ title: e?.message || "Xác nhận thất bại", status: "error", duration: 3000, position: "top" });
      }
    },
    [toast, fetchList]
  );

  const openDetail = useCallback(
    (item) => {
      navigate(`${EMP_BASE_PATH}/danh-gia-ca-nhan/chi-tiet?month=${item.month}&year=${item.year}`);
    },
    [navigate]
  );

  const pageBody = (
    <>

      <Box px="16px" pt="12px">
        {/* Bộ lọc chi tiết */}
        <Flex
          as="button"
          w="100%"
          h="44px"
          px="12px"
          align="center"
          justify="space-between"
          bg="white"
          border="1px solid"
          borderColor={BORDER}
          borderTopRadius="12px"
          borderBottomRadius={filterOpen ? "0" : "12px"}
          onClick={() => setFilterOpen((p) => !p)}
        >
          <Flex align="center" gap="8px">
            <FiSliders size={16} color={TEXT} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>
              Bộ lọc chi tiết
            </Text>
          </Flex>
          {filterOpen ? <FiChevronUp size={18} color={MUTED} /> : <FiChevronDown size={18} color={MUTED} />}
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
            <Box w="48%">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Năm
              </Text>
              <Flex
                as="button"
                w="100%"
                h="40px"
                px="10px"
                align="center"
                justify="space-between"
                bg="#F7F9FC"
                borderRadius="8px"
                onClick={yearSheet.onOpen}
              >
                <Text fontSize="14px" color={TEXT}>
                  Năm {year}
                </Text>
                <FiChevronDown size={18} color={MUTED} />
              </Flex>
            </Box>
          </Box>
        </Collapse>

        {/* Số lượng */}
        <Box mt="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
          <Text fontSize="12px" color={MUTED} fontWeight={600}>
            SỐ LƯỢNG ĐÁNH GIÁ
          </Text>
          <Text fontSize="22px" fontWeight={800} color={APP.primary200} mt="6px">
            {items.length || 0}
          </Text>
        </Box>

        <Text fontSize="14px" fontWeight={600} color={TEXT} mt="16px" mb="12px">
          Danh sách đánh giá
        </Text>

        {loading && !items.length ? (
          <Flex justify="center" py="32px">
            <Spinner color={APP.primary200} />
          </Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="20px">
            Chưa có đánh giá nào trong năm này
          </Text>
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it) => (
              <EvaluationCard
                key={it._id}
                item={it}
                confirmWindow={confirmWindow}
                onOpen={() => openDetail(it)}
                onConfirm={() => onConfirm(it)}
              />
            ))}
          </Flex>
        )}
      </Box>

      {/* Bottom sheet chọn năm */}
      <Drawer isOpen={yearSheet.isOpen} onClose={yearSheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">
              Chọn năm
            </Text>
            {yearOptions.map((y, i) => {
              const active = y === year;
              const last = i === yearOptions.length - 1;
              return (
                <Flex
                  key={y}
                  as="button"
                  w="100%"
                  align="center"
                  justify="space-between"
                  py="12px"
                  borderBottom={last ? "none" : "1px solid #EEF1F6"}
                  onClick={() => {
                    setYear(y);
                    yearSheet.onClose();
                  }}
                >
                  <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400}>
                    Năm {y}
                  </Text>
                </Flex>
              );
            })}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Đánh giá cá nhân" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Đánh giá cá nhân" />
      {pageBody}
    </Box>
  );
};

export default MyEvaluationList;
